import React from "react";
import { TrendingUp, TrendingDown, Pencil, Trash2 } from "lucide-react";
import { PriceAlertConfigData } from "@/api/price-alerts";

interface PriceAlertConfigTileProps {
  config: PriceAlertConfigData;
  onEdit: (config: PriceAlertConfigData) => void;
  onDelete: (configId: string) => void;
}

export const PriceAlertConfigTile: React.FC<PriceAlertConfigTileProps> = ({
  config,
  onEdit,
  onDelete,
}) => {
  const isSpike = config.alertType === "SPIKE";
  const isDisabled = config.status === "DISABLED";

  return (
    <div
      className={`bg-secondary rounded-lg p-3 border border-gray-500/30 ${
        isDisabled ? "opacity-60" : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-500/30 pb-2">
        <div className="flex items-center gap-2">
          {isSpike ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <h3 className="text-lg font-bold text-text-primary">
            {config.tradingPair || "All Pairs"}
          </h3>
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              isSpike ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
            }`}
          >
            {isSpike ? "Spike" : "Drop"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-accent rounded-full px-2 py-0.5 bg-accent/10 capitalize">
            {config.exchange}
          </span>
          {isDisabled && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
              Disabled
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 pt-2">
        <div className="border-r border-gray-500/30">
          <p className="text-xs text-text-secondary mb-1">Threshold</p>
          <p className="text-sm text-text-primary font-medium">
            {config.changeThreshold}%
          </p>
        </div>
        <div>
          <p className="text-xs text-text-secondary mb-1">Timeframe</p>
          <p className="text-sm text-text-primary font-medium">{config.timeframe}</p>
        </div>
      </div>

      {/* Action buttons — similar to CryptoAlertTile */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-500/30">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(config);
          }}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-600/20 text-text-primary hover:bg-gray-600/30 transition-colors text-sm font-medium"
        >
          <Pencil className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(config.id);
          }}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors text-sm font-medium"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </div>
  );
};
